import { createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";
import { format, parse, parseISO } from "date-fns";

const meetingAdapter = createEntityAdapter({
  selectId: (meeting) => meeting.meetingId,
  sortComparer: (a, b) => b.meetingId - a.meetingId,
});

const meetingSummaryAdapter = createEntityAdapter({
  selectId: (meetingSummary) => meetingSummary.id,
});

const initialState = meetingAdapter.getInitialState();
const meetingSummaryInitialState = meetingSummaryAdapter.getInitialState();

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllMeetings: builder.query({
      query: `/Meeting`,
      transformResponse: (responseData) => {
        const loadedMeetings = responseData.map((meeting) => {
          if (meeting?.meetingDate)
            meeting.meetingDate = format(
              parse(meeting.meetingDate, "M/d/yyyy", new Date()),
              "d MMMM yyyy"
            );
          return meeting;
        });
        return meetingAdapter.setAll(initialState, responseData);
      },
      providesTags: (result, error, arg) => [
        { type: "Meeting", id: "LIST" },
        ...result.ids.map((id) => ({ type: "Meeting", id })),
      ],
    }),
    getMeetingsByType: builder.query({
      query: ({ type, startDate, endDate }) =>
        `/Meeting/ByType/${type}/${startDate}/${endDate}`,
      transformResponse: (responseData) => {
        const loadedMeetings = responseData.map((meeting) => {
          if (meeting?.meetingDate)
            meeting.meetingDate = format(
              parseISO(meeting.meetingDate),
              "d MMMM yyyy"
            );
          return meeting;
        });
        return meetingAdapter.setAll(initialState, loadedMeetings);
      },
      providesTags: (result, error, arg) => [
        { type: "Meeting", id: "LIST" },
        ...result.ids.map((id) => ({ type: "Meeting", id })),
      ],
    }),
    getMeetingsByUserId: builder.query({
      query: (userId) => `/Meeting/ByUser/${userId}`,
      transformResponse: (responseData) => {
        const loadedMeetings = responseData.map((meeting) => {
          if (meeting?.meetingDate)
            meeting.meetingDate = format(
              parse(meeting.meetingDate, "M/d/yyyy", new Date()),
              "d MMMM yyyy"
            );
          return meeting;
        });
        return meetingAdapter.setAll(initialState, loadedMeetings);
      },
      providesTags: (result, error, arg) => [
        { type: "Meeting", id: "LIST" },
        ...result.ids.map((id) => ({ type: "Meeting", id })),
      ],
    }),
    getMeetingsSummaryByUserId: builder.query({
      query: ({ userId, type, startDate, endDate }) =>
        `/Meeting/meetings_summary/${userId}/${type}/${startDate}/${endDate}`,
      transformResponse: (responseData) => responseData,
      providesTags: (result, error, arg) => [
        { type: "MeetingSummary", id: "DEFAULT_ID" },
      ],
    }),
    addNewMeeting: builder.mutation({
      query: (initialMeeting) => ({
        url: "/Meeting",
        method: "POST",
        body: {
          ...initialMeeting,
        },
      }),
      invalidatesTags: [
        { type: "Meeting", id: "LIST" },
        { type: "Notification" },
        { type: "MeetingSummary" },
      ],
    }),
    deleteMeeting: builder.mutation({
      query: (meetingId) => ({
        url: `/Meeting/delete/${meetingId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Meeting", id: "LIST" },
        { type: "Notification" },
        { type: "MeetingSummary" },
      ],
    }),
  }),
});

export const {
    useGetAllMeetingsQuery,
    useGetMeetingsByUserIdQuery,
    useGetMeetingsByTypeQuery,
    useAddNewMeetingMutation,
    useDeleteMeetingMutation,
    useGetMeetingsSummaryByUserIdQuery,
} = extendedApiSlice;